import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router";
import { brandInitials } from "@gatiq/shared";
import { useAuth } from "@/auth/AuthProvider";
import { useTenant } from "@/tenant/TenantProvider";
import { ChevronDown, Download, Languages, LogOut, Menu, ShieldAlert, X } from "lucide-react";
import { useInstallPrompt } from "@/lib/useInstallPrompt";
import { LanguageSwitch } from "@/i18n/LanguageSwitch";
import { navFor, type NavItem } from "./nav";
import { AppFooter } from "./AppFooter";
import { PageChromeProvider } from "./PageChrome";
import { Button } from "@/ui";
import { ROLE_LABELS } from "@gatiq/shared";
import { api } from "@/lib/api";
import { useOrgRoles } from "@/auth/useOrgRoles";

/**
 * The signed-in chrome.
 *
 * Design notes:
 *
 *  • The page portals its heading and actions INTO the header (PageChrome),
 *    so there is one row of chrome, not a header plus a repeated <h1>.
 *  • The sidebar is the organization's identity: its mark and name lead, and
 *    the whole rail follows the tenant's `sidebarStyle` token — light, dark
 *    (the sign-in panel colour) or brand — so an org's workspace reads as
 *    theirs at a glance.
 *  • Nav items are grouped under quiet section labels. Eleven undifferentiated
 *    rows were a list to read; three short groups are a map to scan.
 *  • The workspace canvas carries a faint wash of the brand colour
 *    (`.gq-canvas`), so every screen is tinted by the org with no extra
 *    coloured element on it.
 */

type SidebarStyle = "light" | "dark" | "brand";

interface SidebarSkin {
  root: string;
  mark: string;
  name: string;
  sub: string;
  section: string;
  item: string;
  active: string;
  icon: string;
  iconActive: string;
  rail: string;
  foot: string;
}

/*
 * Each skin uses only tokens that are guaranteed readable on its own ground:
 * panel text on the panel, on-brand text on the brand fill. Nothing here can
 * be made unreadable by an org's colour choice.
 */
const SKINS: Record<SidebarStyle, SidebarSkin> = {
  light: {
    root: "bg-raised border-r border-rule",
    mark: "bg-brand text-on-brand",
    name: "text-ink",
    sub: "text-muted",
    section: "text-muted/80",
    item: "text-muted hover:bg-ink/[0.045] hover:text-ink",
    active: "bg-brand-light text-brand-ink font-medium",
    icon: "text-muted group-hover:text-ink",
    iconActive: "text-brand",
    rail: "bg-brand",
    foot: "border-rule text-muted",
  },
  dark: {
    root: "bg-panel",
    mark: "bg-brand text-on-brand",
    name: "text-panel-text",
    sub: "text-panel-muted",
    section: "text-panel-muted",
    item: "text-panel-text/70 hover:bg-panel-text/[0.07] hover:text-panel-text",
    active: "bg-panel-text/[0.11] text-panel-text font-medium",
    icon: "text-panel-text/50 group-hover:text-panel-text",
    iconActive: "text-brand-on-panel",
    rail: "bg-brand-on-panel",
    foot: "border-panel-text/10 text-panel-muted",
  },
  brand: {
    root: "bg-brand",
    mark: "bg-on-brand text-brand",
    name: "text-on-brand",
    sub: "text-on-brand/75",
    section: "text-on-brand/65",
    item: "text-on-brand/80 hover:bg-on-brand/10 hover:text-on-brand",
    active: "bg-on-brand/[0.16] text-on-brand font-medium",
    icon: "text-on-brand/70 group-hover:text-on-brand",
    iconActive: "text-on-brand",
    rail: "bg-on-brand",
    foot: "border-on-brand/15 text-on-brand/75",
  },
};

export function AppShell() {
  const { user, logout } = useAuth();
  const { payload } = useTenant();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { available, installed, prompt } = useInstallPrompt();
  const [prompting, setPrompting] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent): void => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  // Names the team pages and says which tiers exist; refetched when an admin changes them.
  const roles = useOrgRoles();

  if (!user) return null;

  const items = navFor(user, roles);
  const roleLabel = user.roleName ?? ROLE_LABELS[user.role];
  const brand = payload.branding;
  const style: SidebarStyle =
    brand.sidebarStyle === "dark" || brand.sidebarStyle === "brand" ? brand.sidebarStyle : "light";
  const skin = SKINS[style];
  const inherited = style === "light" ? brand.logo : brand.logoDark || brand.logo;
  /* Never wear the platform's mark. An org with no logo of its own shows its
   * initials rather than inheriting GatiQ's. */
  const logo = payload.hasOwnLogo ? inherited : null;
  const orgName = payload.orgName ?? brand.appName;

  const handleLogout = async (): Promise<void> => {
    await logout();
    navigate(user.orgSlug ? `/${user.orgSlug}/login` : "/login", { replace: true });
  };

  /*
   * D-15 — the shell is exactly one viewport tall and never scrolls.
   * `min-h-0` on every flex ancestor is what lets the inner scroll region
   * shrink instead of pushing the page taller.
   */
  return (
    <PageChromeProvider>
      {(setHeaderSlot) => (
        <div className="flex h-dvh flex-col overflow-hidden bg-surface">
          {user.impersonatedBy ? (
            <ImpersonationBanner expiresAt={user.impersonationExpiresAt} name={user.name} />
          ) : null}

          <div className="flex min-h-0 flex-1">
            {/*
             * Scrolling lives on the aside, not the <nav>: an `overflow-y` on
             * the nav would also clip horizontally and slice off the rail.
             */}
            <aside
              className={`hidden h-full w-[16rem] shrink-0 flex-col overflow-y-auto md:flex ${skin.root}`}
            >
              <div className="flex h-[4.25rem] shrink-0 items-center gap-3 px-5">
                {/*
                 * A logo already carries the wordmark, so printing the name
                 * beside it says the same thing twice. When one is set the mark
                 * stands alone with the role beneath it; the name only steps in
                 * for orgs falling back to initials.
                 */}
                {logo ? (
                  <div className="flex min-w-0 flex-col">
                    <img
                      alt={orgName}
                      className="h-8 max-w-[11rem] self-start object-contain"
                      src={logo}
                    />
                    <p className={`mt-1 truncate text-xs ${skin.sub}`}>{roleLabel}</p>
                  </div>
                ) : (
                  <>
                    <span
                      aria-hidden="true"
                      className={`grid size-9 shrink-0 place-items-center rounded-md font-title text-base font-bold shadow-[inset_0_1px_0_rgb(255_255_255/0.18)] ${skin.mark}`}
                    >
                      {brandInitials(orgName)}
                    </span>
                    <div className="min-w-0">
                      <p className={`truncate font-title text-[15px] leading-tight font-semibold ${skin.name}`}>
                        {orgName}
                      </p>
                      <p className={`mt-0.5 truncate text-xs ${skin.sub}`}>{roleLabel}</p>
                    </div>
                  </>
                )}
              </div>

              <nav aria-label="Main" className="flex flex-1 flex-col px-3 pt-2 pb-4">
                {items.map((item, i) => {
                  const Icon = item.icon;
                  const heading = item.section && item.section !== items[i - 1]?.section ? item.section : null;
                  // An item is matched exactly only when another item lives
                  // beneath it (Overview, Dashboard). Everything else stays lit
                  // on its sub-pages — Settings on /settings/identity, etc.
                  const exact = items.some((o) => o !== item && o.to.startsWith(`${item.to}/`));
                  return (
                    <div className="contents" key={item.to}>
                      {heading ? (
                        <p
                          className={`px-3 pt-5 pb-1.5 text-[10.5px] font-semibold tracking-[0.12em] uppercase ${skin.section}`}
                        >
                          {heading}
                        </p>
                      ) : null}
                      <NavLink
                        className={({ isActive }) =>
                          [
                            "group relative mb-0.5 flex h-9 items-center gap-3 rounded-md px-3 text-[13.5px] transition-colors",
                            isActive ? skin.active : skin.item,
                          ].join(" ")
                        }
                        end={exact}
                        to={item.to}
                      >
                        {({ isActive }) => (
                          <>
                            {isActive ? (
                              <span
                                aria-hidden="true"
                                className={`absolute top-2 bottom-2 -left-3 w-[3px] rounded-r-full ${skin.rail}`}
                              />
                            ) : null}
                            <Icon
                              aria-hidden="true"
                              className={`size-[1.0625rem] shrink-0 transition-colors ${
                                isActive ? skin.iconActive : skin.icon
                              }`}
                              strokeWidth={isActive ? 2.25 : 1.85}
                            />
                            <span className="truncate">{item.label}</span>
                          </>
                        )}
                      </NavLink>
                    </div>
                  );
                })}
              </nav>

              {/* `mt-auto` pins this to the foot of the scroll area. */}
              <div className={`mt-auto flex items-center gap-2 border-t px-5 py-3.5 text-xs ${skin.foot}`}>
                <ModuleGlyph />
                <span className="truncate">{brand.appName}</span>
              </div>
            </aside>

            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              <header className="relative z-30 flex h-[4.25rem] shrink-0 items-center gap-4 border-b border-rule bg-raised/90 px-5 backdrop-blur-sm lg:px-8">
                <MobileNav items={items} label={brand.appName ?? "Menu"} />
                {/* The page portals its title and actions in here (PageHeader). */}
                <div className="min-w-0 flex-1" ref={setHeaderSlot} />

                {available && !installed ? (
                  /* Was `hidden … sm:flex`, which hid it on exactly the phone
                     most likely to want it installed. Now it shows everywhere;
                     below `sm` it drops the label and keeps the icon, because
                     the header's width belongs to the page title. */
                  <button
                    aria-label="Install app"
                    className="flex h-9 shrink-0 items-center gap-2 rounded-lg bg-brand px-2.5 text-sm font-medium text-on-brand shadow-sm transition hover:opacity-90 sm:px-3.5"
                    disabled={prompting}
                    onClick={async () => {
                      setPrompting(true);
                      await prompt();
                      setPrompting(false);
                    }}
                    title="Install app"
                    type="button"
                  >
                    <Download aria-hidden="true" className="size-4" />
                    <span className="hidden sm:inline">
                      {prompting ? "Installing…" : "Install app"}
                    </span>
                  </button>
                ) : null}

                <div className="relative shrink-0" ref={menuRef}>
                  <button
                    aria-expanded={menuOpen}
                    aria-haspopup="menu"
                    className="flex items-center gap-2.5 rounded-full py-1 pr-2.5 pl-1 text-sm text-ink ring-1 ring-transparent transition-all hover:bg-ink/[0.04] hover:ring-rule"
                    onClick={() => setMenuOpen((o) => !o)}
                    type="button"
                  >
                    {user.photoUrl ? (
                      <img
                        alt=""
                        className="size-8 shrink-0 rounded-full object-cover shadow-[inset_0_1px_0_rgb(255_255_255/0.18)]"
                        src={user.photoUrl}
                      />
                    ) : (
                      <span className="grid size-8 place-items-center rounded-full bg-brand font-title text-[11px] font-semibold text-on-brand shadow-[inset_0_1px_0_rgb(255_255_255/0.18)]">
                        {initials(user.name)}
                      </span>
                    )}
                    <span className="hidden max-w-[11rem] min-w-0 text-left leading-tight sm:block">
                      <span className="block truncate text-[13px] font-medium text-ink">{user.name}</span>
                      <span className="block truncate text-[11px] text-muted">{roleLabel}</span>
                    </span>
                    <ChevronDown
                      aria-hidden="true"
                      className={`hidden size-4 text-muted transition-transform sm:block ${menuOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {menuOpen ? (
                    <div
                      className="absolute right-0 z-40 mt-2 w-64 origin-top-right animate-pop rounded-lg border border-rule bg-raised p-1.5 shadow-pop"
                      role="menu"
                    >
                      <div className="flex items-center gap-3 rounded-md bg-ink/[0.03] px-3 py-3">
                        {user.photoUrl ? (
                          <img
                            alt=""
                            className="size-9 shrink-0 rounded-full object-cover"
                            src={user.photoUrl}
                          />
                        ) : (
                          <span className="grid size-9 shrink-0 place-items-center rounded-full bg-brand font-title text-xs font-semibold text-on-brand">
                            {initials(user.name)}
                          </span>
                        )}
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-ink">{user.name}</p>
                          <p className="truncate text-xs text-muted">
                            {user.email ?? user.phone ?? roleLabel}
                          </p>
                          {user.displayId ? (
                            <p className="mt-0.5 font-mono text-[11px] text-muted">{user.displayId}</p>
                          ) : null}
                        </div>
                      </div>
                      <div className="mt-1 flex items-center justify-between gap-3 border-b border-rule px-3 py-2.5">
                        <span className="flex items-center gap-2.5 text-sm text-ink">
                          <Languages aria-hidden="true" className="size-4 text-muted" />
                          Language
                        </span>
                        <LanguageSwitch />
                      </div>
                      <button
                        className="mt-1 flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm text-ink transition-colors hover:bg-brand-light hover:text-brand-ink"
                        onClick={() => void handleLogout()}
                        role="menuitem"
                        type="button"
                      >
                        <LogOut aria-hidden="true" className="size-4" />
                        Sign out
                      </button>
                    </div>
                  ) : null}
                </div>
              </header>

              {/* `overflow-hidden` here is the backstop: if a page forgets its
                  <Page> wrapper the overflow is clipped rather than turning into
                  a document scroll. */}
              {/* A list page (`<Page bleed>`) takes the whole area: no padding, no width cap. */}
              <main className="gq-canvas flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-5 py-5 has-[[data-bleed]]:p-0 has-[[data-document-page]]:overflow-y-auto lg:px-8 lg:has-[[data-full-width]]:px-6">
                <div className="mx-auto flex min-h-0 w-full max-w-[var(--gq-container)] flex-1 flex-col has-[[data-bleed]]:max-w-none has-[[data-full-width]]:max-w-none has-[[data-document-page]]:flex-none">
                  <Outlet />
                </div>
              </main>

              <AppFooter className="shrink-0 border-t border-rule bg-raised/60 px-5 py-2 lg:px-8" />
            </div>
          </div>
        </div>
      )}
    </PageChromeProvider>
  );
}

/** Three QR modules in an L — the product's mark, at the foot of the rail. */
function ModuleGlyph() {
  return (
    <span aria-hidden="true" className="grid shrink-0 grid-cols-2 gap-[2px] opacity-70">
      <span className="size-[4px] rounded-[1px] bg-current" />
      <span className="size-[4px] rounded-[1px] bg-current" />
      <span className="size-[4px] rounded-[1px] bg-current" />
      <span className="size-[4px]" />
    </span>
  );
}

/**
 * D-10 / ARCHITECTURE §5.4 — a live countdown to the 30-minute expiry. At
 * zero, `refresh()` re-asks `/auth/me`, which rejects the expired
 * impersonation cookie and drops the caller back to their own session.
 */
function ImpersonationBanner({ name, expiresAt }: { name: string; expiresAt: number | null }) {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [exiting, setExiting] = useState(false);
  const [remainingMs, setRemainingMs] = useState<number | null>(
    expiresAt !== null ? expiresAt - Date.now() : null,
  );

  useEffect(() => {
    if (expiresAt === null) {
      setRemainingMs(null);
      return;
    }

    let ended = false;

    const tick = (): void => {
      const left = expiresAt - Date.now();
      setRemainingMs(left);
      if (left <= 0 && !ended) {
        ended = true;
        clearInterval(intervalId);
        void refresh();
      }
    };

    const intervalId = setInterval(tick, 1000);
    tick();

    return () => clearInterval(intervalId);
  }, [expiresAt, refresh]);

  const exit = async (): Promise<void> => {
    setExiting(true);
    await api.del("/platform/impersonate");
    await refresh();
    navigate("/super-admin/organizations", { replace: true });
  };

  return (
    <div className="flex flex-wrap items-center justify-center gap-3 bg-danger bg-[repeating-linear-gradient(135deg,transparent_0_12px,rgb(0_0_0/0.08)_12px_24px)] px-4 py-2 text-sm text-white">
      <ShieldAlert aria-hidden="true" className="size-4 shrink-0" />
      <span>
        Viewing as <strong>{name}</strong>
        {remainingMs !== null ? (
          <>
            {" · ends in "}
            <span className="font-semibold tabular-nums">{formatCountdown(remainingMs)}</span>
          </>
        ) : null}
      </span>
      <Button loading={exiting} onClick={() => void exit()} size="sm" variant="secondary">
        Exit
      </Button>
    </div>
  );
}

/** `m:ss`, clamped at zero — a plain ticking number, no animation. */
function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, "0")}`;
}

function MobileNav({ items, label }: { items: NavItem[]; label: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="md:hidden">
      <button
        aria-expanded={open}
        aria-label={`Menu — ${label}`}
        className="-ml-1 grid size-9 place-items-center rounded-md text-ink transition-colors hover:bg-ink/[0.05]"
        onClick={() => setOpen((o) => !o)}
        type="button"
      >
        {open ? <X aria-hidden="true" className="size-5" /> : <Menu aria-hidden="true" className="size-5" />}
      </button>
      {open ? (
        <div className="absolute inset-x-0 top-full z-40 flex max-h-[75dvh] animate-fade flex-col gap-0.5 overflow-y-auto border-b border-rule bg-raised p-3 shadow-pop">
          {items.map((item, i) => {
            const Icon = item.icon;
            const heading = item.section && item.section !== items[i - 1]?.section ? item.section : null;
            return (
              <div className="contents" key={item.to}>
                {heading ? (
                  <p className="px-3 pt-3 pb-1 text-[10.5px] font-semibold tracking-[0.12em] text-muted uppercase">
                    {heading}
                  </p>
                ) : null}
                <NavLink
                  className={({ isActive }) =>
                    [
                      "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors",
                      isActive
                        ? "bg-brand-light font-medium text-brand-ink"
                        : "text-ink hover:bg-ink/[0.045]",
                    ].join(" ")
                  }
                  end={items.some((o) => o !== item && o.to.startsWith(`${item.to}/`))}
                  onClick={() => setOpen(false)}
                  to={item.to}
                >
                  <Icon aria-hidden="true" className="size-[1.125rem] shrink-0" strokeWidth={1.85} />
                  {item.label}
                </NavLink>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}
